import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { API_BASE_URL, api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

const TABS = [
  { key: 'general', label: 'General' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'api', label: 'API Access' },
  { key: 'security', label: 'Security' },
];

export default function SettingsPage() {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [apiStatus, setApiStatus] = useState<'unknown' | 'online' | 'offline'>('unknown');
  const [apiStatusLoading, setApiStatusLoading] = useState(false);

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const selectBg = isDark ? '#1A1A1A' : '#fff';
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const separator = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const tabBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';

  const checkApiStatus = async () => {
    setApiStatusLoading(true);
    try {
      await api.get('/counterparties');
      setApiStatus('online');
      addToast('API is online and responding', 'success');
    } catch (err) {
      setApiStatus('offline');
      const msg = getApiErrorMessage(err, 'API unreachable');
      addToast(msg, 'error');
    } finally {
      setApiStatusLoading(false);
    }
  };

  useEffect(() => {
    checkApiStatus();
  }, []);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      addToast('Settings saved successfully', 'success');
    }, 800);
  };

  return (
    <DashboardLayout breadcrumb={['Settings']}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
          Settings
        </h1>
        <p className="text-sm mt-1" style={{ color: textMuted }}>
          Manage your account and system preferences
        </p>
      </div>

      <div className="flex items-center gap-0 mb-6 overflow-x-auto" style={{ borderBottom: `1px solid ${tabBorder}` }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="px-4 py-3 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap relative"
            style={{
              color: activeTab === tab.key ? '#D12229' : textMuted,
              borderBottom: activeTab === tab.key ? '2px solid #D12229' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        {activeTab === 'general' && (
          <div className="max-w-xl space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Interface Language</label>
              <select className="w-full px-3 py-2.5 rounded-md text-sm border outline-none" style={{ borderColor: inputBorder, background: selectBg, color: textMain }}>
                <option>Українська</option>
                <option>English</option>
                <option>Polski</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Time Zone</label>
              <select className="w-full px-3 py-2.5 rounded-md text-sm border outline-none" style={{ borderColor: inputBorder, background: selectBg, color: textMain }}>
                <option>Europe/Kyiv (EET, UTC+2)</option>
                <option>Europe/Warsaw (CET, UTC+1)</option>
                <option>UTC</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Date Format</label>
              <select className="w-full px-3 py-2.5 rounded-md text-sm border outline-none" style={{ borderColor: inputBorder, background: selectBg, color: textMain }}>
                <option>DD.MM.YYYY</option>
                <option>YYYY-MM-DD</option>
                <option>MM/DD/YYYY</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Number Format</label>
              <select className="w-full px-3 py-2.5 rounded-md text-sm border outline-none" style={{ borderColor: inputBorder, background: selectBg, color: textMain }}>
                <option>1 234,56 (European)</option>
                <option>1,234.56 (US)</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="max-w-xl space-y-4">
            {[
              { key: 'email_alerts', label: 'Email Alerts', desc: 'Receive critical system alerts via email' },
              { key: 'dispatch_notifications', label: 'Dispatch Notifications', desc: 'Get notified about dispatch schedule changes' },
              { key: 'forecast_updates', label: 'Forecast Updates', desc: 'Daily forecast summary emails' },
              { key: 'market_digest', label: 'Market Digest', desc: 'Weekly market price and trend summary' },
              { key: 'contract_reminders', label: 'Contract Reminders', desc: 'Alerts before contract expiration' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between p-3 rounded-lg" style={{ border: `1px solid ${inputBorder}` }}>
                <div>
                  <div className="text-sm font-medium" style={{ color: textMain }}>{item.label}</div>
                  <div className="text-xs" style={{ color: textMuted }}>{item.desc}</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 rounded-full peer transition-colors" style={{ background: isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8' }}></div>
                </label>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'api' && (
          <div className="max-w-xl space-y-5">
            <div className="p-4 rounded-lg" style={{ background: sectionBg }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium uppercase tracking-wider" style={{ color: textMuted }}>API Base URL</div>
                <div className="flex items-center gap-2">
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{
                      background: apiStatus === 'online' ? 'rgba(13,122,60,0.1)' : apiStatus === 'offline' ? 'rgba(209,34,41,0.1)' : 'rgba(107,107,112,0.1)',
                      color: apiStatus === 'online' ? '#0D7A3C' : apiStatus === 'offline' ? '#D12229' : '#6B6B70',
                    }}
                  >
                    {apiStatus === 'unknown' ? 'Checking...' : apiStatus === 'online' ? 'Online' : 'Offline'}
                  </span>
                  <button
                    onClick={checkApiStatus}
                    disabled={apiStatusLoading}
                    className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer transition-colors disabled:opacity-50"
                    style={{ border: `1px solid ${inputBorder}` }}
                    title="Test API connection"
                  >
                    <i className={`ri-refresh-line text-xs ${apiStatusLoading ? 'animate-spin' : ''}`} style={{ color: textMuted }}></i>
                  </button>
                </div>
              </div>
              <div className="font-mono text-sm" style={{ color: textMain }}>{API_BASE_URL}</div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>API Key</label>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value="sk_live_xxxxxxxxxxxxxxxxxxxxxxxx"
                  readOnly
                  className="flex-1 px-3 py-2.5 rounded-md text-sm border outline-none font-mono"
                  style={{ borderColor: inputBorder, background: sectionBg, color: textMuted }}
                />
                <button
                  onClick={() => addToast('API key copied to clipboard', 'info')}
                  className="px-3 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                  style={{ border: `1px solid ${inputBorder}`, color: textMain }}
                >
                  <i className="ri-file-copy-line mr-1"></i> Copy
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Webhook URL</label>
              <input
                type="text"
                placeholder="https://your-domain.com/webhook"
                className="w-full px-3 py-2.5 rounded-md text-sm border outline-none"
                style={{ borderColor: inputBorder, background: selectBg, color: textMain }}
              />
            </div>
          </div>
        )}

        {activeTab === 'security' && (
          <div className="max-w-xl space-y-5">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Current Password</label>
              <input type="password" className="w-full px-3 py-2.5 rounded-md text-sm border outline-none" style={{ borderColor: inputBorder, background: selectBg, color: textMain }} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>New Password</label>
              <input type="password" className="w-full px-3 py-2.5 rounded-md text-sm border outline-none" style={{ borderColor: inputBorder, background: selectBg, color: textMain }} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>Two-Factor Authentication</label>
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ border: `1px solid ${inputBorder}` }}>
                <div className="text-sm" style={{ color: textMain }}>Enable 2FA via authenticator app</div>
                <button className="px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap" style={{ background: '#D12229', color: '#fff' }}>
                  Enable
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end mt-8 pt-6" style={{ borderTop: `1px solid ${separator}` }}>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
            style={{ background: '#D12229' }}
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i> Saving...
              </span>
            ) : (
              <span><i className="ri-save-line mr-1"></i> Save Settings</span>
            )}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}